import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, MapPin, Truck, CreditCard } from 'lucide-react';

/* ────── Internal helpers ────── */
const Section = ({ title, icon: Icon, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="bg-card rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
      >
        <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
          {Icon && <Icon className="h-4 w-4" />} {title}
        </h3>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && <div className="px-4 pb-4 pt-0">{children}</div>}
    </div>
  );
};

const Field = ({ label, type = 'text', value, onChange, placeholder, testId, ...rest }) => (
  <div>
    <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-input rounded-md text-sm"
      data-testid={testId}
      {...rest}
    />
  </div>
);

/**
 * Order Details / Billing / Shipping / Tax & Freight panel for the
 * Create Sales Order page. All state lives in the parent's `details`
 * object — this component is purely presentational.
 */
export const OrderDetailsForm = ({
  details,
  setField,
  setNumField,
  setDetails,
  // Lease autocomplete
  leaseDropdownRef,
  showLeaseDropdown,
  setShowLeaseDropdown,
  leaseNameResults,
  handleLeaseNameChange,
  selectLeaseName,
}) => {
  return (
    <>
      {/* Order Details Section */}
      <Section title="Order Details" icon={FileText} defaultOpen={true}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Order Date" type="date" value={details.order_date} onChange={setField('order_date')} testId="order-date" />

          {/* Picked Up By (Required) */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Picked Up By *</label>
            <input
              type="text"
              value={details.picked_up_by || ''}
              onChange={setField('picked_up_by')}
              placeholder="Name of person picking up"
              className={`w-full px-3 py-2 border rounded-md text-sm ${!(details.picked_up_by || '').trim() ? 'border-red-300 bg-red-50/30' : 'border-input'}`}
              data-testid="picked-up-by"
            />
          </div>

          {/* Lease Name with Autocomplete */}
          <div className="col-span-2 md:col-span-3 relative" ref={leaseDropdownRef}>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Lease Name</label>
            <input
              type="text"
              value={details.lease_name || ''}
              onChange={handleLeaseNameChange}
              onFocus={() => { if (details.lease_name && leaseNameResults.length > 0) setShowLeaseDropdown(true); }}
              placeholder="Enter or search lease name..."
              className="w-full px-3 py-2 border border-input rounded-md text-sm"
              data-testid="lease-name-input"
            />
            {showLeaseDropdown && leaseNameResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto" data-testid="lease-name-dropdown">
                {leaseNameResults.map((name, i) => (
                  <button
                    key={i}
                    onClick={() => selectLeaseName(name)}
                    className="w-full text-left px-3 py-2 hover:bg-muted transition-colors text-sm border-b border-border last:border-b-0"
                    data-testid={`lease-name-option-${i}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Field label="PO Number" value={details.po_number} onChange={setField('po_number')} placeholder="Customer PO #" testId="po-number" />
          <Field label="Sales Rep" value={details.sales_rep} onChange={setField('sales_rep')} placeholder="Sales rep name" testId="sales-rep" />
          <Field label="Location" value={details.location} onChange={setField('location')} placeholder="Warehouse / location" testId="location" />

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Order Status</label>
            <select
              value={details.status}
              onChange={setField('status')}
              className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
              data-testid="order-status-select"
            >
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="quote">Quote</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Payment Terms</label>
            <select
              value={details.payment_terms}
              onChange={(e) => {
                const terms = e.target.value;
                let dueDays = 30;
                const m = terms.match(/Net\s*(\d+)/i);
                if (m) dueDays = parseInt(m[1]);
                else if (terms.toLowerCase().includes('receipt') || terms.toLowerCase().includes('cod')) dueDays = 0;
                const dueDate = new Date();
                dueDate.setDate(dueDate.getDate() + dueDays);
                setDetails(prev => ({ ...prev, payment_terms: terms, due_date: dueDate.toISOString().split('T')[0] }));
              }}
              className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
              data-testid="payment-terms"
            >
              <option value="Due Upon Receipt">Due Upon Receipt</option>
              <option value="Net 15">Net 15</option>
              <option value="Net 30">Net 30</option>
              <option value="Net 45">Net 45</option>
              <option value="Net 60">Net 60</option>
              <option value="Net 90">Net 90</option>
            </select>
          </div>

          <Field label="Due Date" type="date" value={details.due_date} onChange={setField('due_date')} testId="due-date" />
          <Field label="Requested Ship Date" type="date" value={details.requested_ship_date} onChange={setField('requested_ship_date')} testId="ship-date" />

          <div className="col-span-2 md:col-span-3">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Remarks</label>
            <textarea
              value={details.remarks || ''}
              onChange={setField('remarks')}
              rows="2"
              placeholder="Order notes..."
              className="w-full px-3 py-2 border border-input rounded-md text-sm"
              data-testid="remarks"
            />
          </div>
        </div>
      </Section>

      {/* Billing Address */}
      <Section title="Billing Address" icon={MapPin}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Address Line 1" value={details.billing_address1} onChange={setField('billing_address1')} testId="bill-addr1" />
          <Field label="Address Line 2" value={details.billing_address2} onChange={setField('billing_address2')} testId="bill-addr2" />
          <Field label="City" value={details.billing_city} onChange={setField('billing_city')} testId="bill-city" />
          <Field label="State" value={details.billing_state} onChange={setField('billing_state')} testId="bill-state" />
          <Field label="Postal Code" value={details.billing_postal_code} onChange={setField('billing_postal_code')} testId="bill-zip" />
          <Field label="Country" value={details.billing_country} onChange={setField('billing_country')} testId="bill-country" />
        </div>
      </Section>

      {/* Shipping Address */}
      <Section title="Shipping Address" icon={Truck}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ship To Company" value={details.ship_to_company} onChange={setField('ship_to_company')} testId="ship-company" />
          <Field label="Address Line 1" value={details.shipping_address1} onChange={setField('shipping_address1')} testId="ship-addr1" />
          <Field label="Address Line 2" value={details.shipping_address2} onChange={setField('shipping_address2')} testId="ship-addr2" />
          <Field label="City" value={details.shipping_city} onChange={setField('shipping_city')} testId="ship-city" />
          <Field label="State" value={details.shipping_state} onChange={setField('shipping_state')} testId="ship-state" />
          <Field label="Postal Code" value={details.shipping_postal_code} onChange={setField('shipping_postal_code')} testId="ship-zip" />
          <Field label="Country" value={details.shipping_country} onChange={setField('shipping_country')} testId="ship-country" />
          <Field label="Carrier" value={details.shipping_carrier} onChange={setField('shipping_carrier')} testId="ship-carrier" />
          <Field label="Tracking #" value={details.shipping_tracking_number} onChange={setField('shipping_tracking_number')} testId="ship-tracking" />
        </div>
      </Section>

      {/* Tax & Freight */}
      <Section title="Tax & Freight" icon={CreditCard}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Taxing Scheme</label>
            <select
              value={details.taxing_scheme}
              onChange={setField('taxing_scheme')}
              className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
              data-testid="taxing-scheme"
            >
              <option value="Taxable">Taxable</option>
              <option value="Tax Exempt">Tax Exempt</option>
            </select>
          </div>
          <Field label="Tax 1 Name" value={details.tax1_name} onChange={setField('tax1_name')} testId="tax1-name" />
          <Field label="Tax 1 Rate (%)" type="number" step="0.01" value={details.tax1_rate} onChange={setNumField('tax1_rate')} testId="tax1-rate" />
          <Field label="Tax 2 Name" value={details.tax2_name} onChange={setField('tax2_name')} testId="tax2-name" />
          <Field label="Tax 2 Rate (%)" type="number" step="0.01" value={details.tax2_rate} onChange={setNumField('tax2_rate')} testId="tax2-rate" />
          <Field label="Freight ($)" type="number" step="0.01" value={details.freight} onChange={setNumField('freight')} testId="freight" />
        </div>
      </Section>
    </>
  );
};
